import { useTranslation } from 'react-i18next';
import { Calendar, Clock, MapPin, User, Edit2, X, Loader, StickyNote } from 'lucide-react';

// Color map for meeting types - gives each type a distinct left border color
const MEETING_TYPE_COLORS = {
  'initial': '#6366f1',      // indigo - first meeting
  'follow-up': '#0ea5e9',    // sky blue - ongoing
  'assessment': '#f59e0b',   // amber - evaluations
  'consultation': '#8b5cf6', // violet - consultations
  'group': '#10b981',        // emerald - group sessions
  'emergency': '#ef4444',    // red - urgent
  'treatment': '#7C3AED',    // violet - treatments
  'default': 'var(--color-primary-dark)'
};

function getMeetingTypeColor(type) {
  if (!type) return MEETING_TYPE_COLORS.default;
  const normalizedType = type.toLowerCase().trim();
  // Check for partial matches in the type string
  for (const [key, color] of Object.entries(MEETING_TYPE_COLORS)) {
    if (key !== 'default' && normalizedType.includes(key)) {
      return color;
    }
  }
  // Generate a stable color from the type string for types not in the map
  let hash = 0;
  for (let i = 0; i < normalizedType.length; i++) {
    hash = normalizedType.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 55%, 50%)`;
}

/**
 * UpcomingMeetingsList - Displays upcoming meetings grouped by date
 * Shows meetings from today to +7 days
 */
export default function UpcomingMeetingsList({ meetings = [], onEdit, onCancel, isLoading = false }) {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  // Group meetings by date
  const groupedMeetings = {};
  const sortedMeetings = [...meetings].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA - dateB;
    }
    return (a.time || '').localeCompare(b.time || '');
  });

  sortedMeetings.forEach((meeting) => {
    const date = new Date(meeting.date);
    const dateKey = date.toISOString().split('T')[0];
    if (!groupedMeetings[dateKey]) {
      groupedMeetings[dateKey] = [];
    }
    groupedMeetings[dateKey].push(meeting);
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return t('student.today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('student.tomorrow');
    } else {
      return date.toLocaleDateString(i18n.language === 'ar' ? 'ar' : i18n.language === 'he' ? 'he' : 'en', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          minHeight: '200px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary-dark)' }} />
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        minHeight: '200px'
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Calendar size={20} color="var(--color-primary-dark)" />
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '600',
          color: 'var(--color-text-primary)',
          margin: 0
        }}>
          {t('student.upcomingMeetings')}
        </h3>
        {meetings.length > 0 && (
          <span style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--color-primary-dark)',
            backgroundColor: 'var(--color-primary-light)',
            padding: '0.25rem 0.625rem',
            borderRadius: '12px',
            minWidth: '24px',
            textAlign: 'center'
          }}>
            {meetings.length}
          </span>
        )}
      </div>

      {/* Meetings List */}
      {meetings.length === 0 ? (
        <div
          style={{
            padding: '3rem 1.5rem',
            textAlign: 'center',
            color: 'var(--color-text-secondary)'
          }}
        >
          <Calendar size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '500' }}>
            {t('student.noUpcomingMeetings')}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem' }}>
            {t('student.scheduledMeetingsAppear')}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {Object.keys(groupedMeetings).map((dateKey) => (
            <div key={dateKey}>
              {/* Date Header */}
              <div
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {formatDate(dateKey)}
              </div>

              {/* Meetings for this date */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {groupedMeetings[dateKey].map((meeting) => {
                  const borderColor = getMeetingTypeColor(meeting.type);
                  return (
                  <div
                    key={meeting.id}
                    style={{
                      padding: '1rem',
                      backgroundColor: 'var(--color-hover)',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      borderLeft: isRTL ? '1px solid var(--color-border)' : `4px solid ${borderColor}`,
                      borderRight: isRTL ? `4px solid ${borderColor}` : '1px solid var(--color-border)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-primary-dark)';
                      e.currentTarget.style.borderLeftColor = isRTL ? 'var(--color-primary-dark)' : borderColor;
                      e.currentTarget.style.borderRightColor = isRTL ? borderColor : 'var(--color-primary-dark)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                      e.currentTarget.style.borderLeftColor = isRTL ? 'var(--color-border)' : borderColor;
                      e.currentTarget.style.borderRightColor = isRTL ? borderColor : 'var(--color-border)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      {/* Meeting Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Student Name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <User size={16} color="var(--color-primary-dark)" />
                          <span style={{
                            fontSize: '0.9375rem',
                            fontWeight: '600',
                            color: 'var(--color-text-primary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {meeting.studentName || t('calendar.student')}
                          </span>
                        </div>

                        {/* Meeting Details */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={14} color="var(--color-text-secondary)" />
                            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                              {meeting.time || '--:--'} - {meeting.endTime || '--:--'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <MapPin size={14} color="var(--color-text-secondary)" />
                            <span style={{
                              fontSize: '0.8125rem',
                              color: 'var(--color-text-secondary)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {meeting.place || t('student.notSpecified')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {meeting.type && (
                            <div style={{
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: 'var(--color-primary-dark)',
                              backgroundColor: 'var(--color-primary-light)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              marginTop: '0.25rem',
                              width: 'fit-content'
                            }}>
                              {meeting.type}
                            </div>
                          )}
                          {meeting.treatmentType && (
                            <div style={{
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              color: '#7C3AED',
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              marginTop: '0.25rem',
                              width: 'fit-content'
                            }}>
                              {meeting.treatmentType}
                            </div>
                          )}
                          {(meeting.content || meeting.notes || meeting.agenda) && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.75rem',
                              color: 'var(--color-text-secondary)',
                              marginTop: '0.25rem'
                            }}
                            title={t('student.hasNotes')}
                            >
                              <StickyNote size={13} />
                            </div>
                          )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button
                          onClick={() => onEdit(meeting)}
                          aria-label={t('student.edit')}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-primary-light)';
                            e.currentTarget.style.borderColor = 'var(--color-primary-dark)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                          }}
                          title={t('student.edit')}
                        >
                          <Edit2 size={16} color="var(--color-primary-dark)" />
                        </button>
                        <button
                          onClick={() => onCancel(meeting)}
                          aria-label={t('student.cancel')}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: 'transparent',
                            border: '1px solid var(--color-border)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#fee2e2';
                            e.currentTarget.style.borderColor = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                          }}
                          title={t('student.cancel')}
                        >
                          <X size={16} color="#ef4444" />
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
